// src/redux/slices/tiffinSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../api/authApi';

// ==================== ASYNC THUNKS ====================

export const addToCartAsync = createAsyncThunk(
  'tiffin/addToCart',
  async (cartItem, { rejectWithValue }) => {
    try {
      const response = await apiService.addToCart(cartItem);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCartItemsAsync = createAsyncThunk(
  'tiffin/fetchCartItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCartItems();
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// ==================== INITIAL STATE ====================

const initialState = {
  cartItems: [],
  orderItems: [],
  orders: [],
  selectedOrder: null,
  isLoading: false,
  error: null,
  totalPrice: 0,
  cartCount: 0,
  selectedDate: null,
  selectedTime: null,
  selectedMealType: 'Lunch',
  orderConfirmed: false,
};

// ==================== SLICE ====================

const tiffinSlice = createSlice({
  name: 'tiffin',
  initialState,
  reducers: {
    // ========== ORDER ITEMS ==========
    addOrderItem: (state, action) => {
      const existingItem = state.orderItems.find(
        (item) => item.id === action.payload.item.id
      );
      if (existingItem) {
        existingItem.quantity += action.payload.item.quantity || 1;
      } else {
        state.orderItems.push({
          ...action.payload.item,
          quantity: action.payload.item.quantity || 1,
        });
      }
    },
    removeOrderItem: (state, action) => {
      state.orderItems = state.orderItems.filter(
        (item) => item.id !== action.payload
      );
    },
    updateItemQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.orderItems.find((item) => item.id === id);
      if (item) {
        item.quantity = quantity;
      }
    },
    clearOrderItems: (state) => {
      state.orderItems = [];
    },
    
    // ========== CART ==========
    clearCartState: (state) => {
      state.cartItems = [];
      state.totalPrice = 0;
      state.cartCount = 0;
    },
    
    // ========== DATE & TIME SELECTION ==========
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedTime: (state, action) => {
      state.selectedTime = action.payload;
    },
    setSelectedMealType: (state, action) => {
      state.selectedMealType = action.payload;
    },
    
    // ========== ORDER CONFIRMATION ==========
    confirmOrder: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    orderConfirmed: (state) => {
      state.isLoading = false;
      state.orderConfirmed = true;
      state.orderItems = [];
      state.selectedDate = null;
      state.selectedTime = null;
      state.selectedMealType = 'Lunch';
    },
    clearSelection: (state) => {
      state.selectedDate = null;
      state.selectedTime = null;
      state.selectedMealType = 'Lunch';
      state.orderItems = [];
      state.orderConfirmed = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== FETCH CART ITEMS ==========
      .addCase(fetchCartItemsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCartItemsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cartItems = action.payload.cartItems || action.payload.data || [];
        state.totalPrice = action.payload.totalPrice || 0;
        state.cartCount = state.cartItems.length;
      })
      .addCase(fetchCartItemsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch cart';
      })
      
      // ========== ADD TO CART ==========
      .addCase(addToCartAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cartItems = action.payload.cartItems || action.payload.data || state.cartItems;
        state.totalPrice = action.payload.totalPrice || state.totalPrice;
        state.cartCount = state.cartItems.length;
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add to cart';
      });
  },
});

// ==================== EXPORT ACTIONS & REDUCER ====================

export const {
  addOrderItem,
  removeOrderItem,
  updateItemQuantity,
  clearOrderItems,
  clearCartState,
  setSelectedDate,
  setSelectedTime,
  setSelectedMealType,
  confirmOrder,
  orderConfirmed,
  clearSelection,
  clearError,
} = tiffinSlice.actions;

export default tiffinSlice.reducer;